import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  HeartPulse,
} from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import YearMonthPicker from '../../../components/yearMonthPicker';
import {
  ComplianceDashboardView,
  DeductionCapBreachView,
  ProbationWarningView,
  ComplianceEventView,
  MonthlyPensionContributionView,
  EmiratizationSummaryView,
  SifPreflightView,
  StatutoryBaseHealthView,
} from '../../../models/payroll';
import {
  getComplianceDashboard,
  getDeductionBreaches,
  getProbationWarnings,
  getComplianceHistory,
  resolveComplianceEvent,
} from '../../../api/Payroll/compliance';
import { getSifPreflight } from '../../../api/Payroll/wps';
import {
  getContributions,
  getEmiratizationSummary,
} from '../../../api/Payroll/gpssa';
import { getGosiPanelData } from '../../../api/Payroll/gosi';
import { OverviewTab } from './tabs/OverviewTab';
import { ComplianceIssuesTab } from './tabs/ComplianceIssuesTab';
import { EventLogTab } from './tabs/EventLogTab';
import { KsaCchiPanel } from './components/KsaCchiPanel';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import { getApiErrorMessage } from '../../../utils/apiErrors';

type TabId = 'overview' | 'cchi' | 'compliance-issues' | 'events';

const ComplianceCenter: React.FC = () => {
  const { t } = useTranslation();
  const country = useComplianceCountry();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dashboard, setDashboard] = useState<ComplianceDashboardView | null>(null);
  const [breaches, setBreaches] = useState<DeductionCapBreachView[]>([]);
  const [probationWarnings, setProbationWarnings] = useState<ProbationWarningView[]>([]);
  const [history, setHistory] = useState<ComplianceEventView[]>([]);

  const [contributions, setContributions] = useState<MonthlyPensionContributionView[]>([]);
  const [emiratization, setEmiratization] = useState<EmiratizationSummaryView | null>(null);
  const [preflight, setPreflight] = useState<SifPreflightView | null>(null);
  // CO-256 — the statutory-base guard. KSA only: the compliance Dashboard endpoint (which carries
  // the UAE equivalent, BasicSalaryConfigCount) is UAE-guarded, so for a KSA tenant `dashboard` is
  // always null and the guard had no delivery path to this screen at all.
  const [statutoryBaseHealth, setStatutoryBaseHealth] = useState<StatutoryBaseHealthView | null>(null);
  /**
   * CO-261 — the GOSI fetch's OWN settle state, because `loading` deliberately does not cover it.
   *
   * `setLoading(false)` fires before the un-awaited GOSI call (see the CO-256/CO-261 comment in
   * `fetchData`), so for the 1-2.5 s that call takes the page renders complete and spinner-free
   * with an EMPTY statutory-base slot — DOM-identical to a tenant whose base is healthy, because
   * StatutoryBaseAlert renders null for a null health. Anything that reads "no alert" in that
   * window (a person or a settle-waiting test) reads a false clean bill of health.
   *
   * `gosiPending` marks "the answer is still in flight"; `gosiResolved` marks "this run's GOSI call
   * reached a terminal state" so that an empty slot can be stated positively rather than inferred
   * from absence. Both are KSA-only: on UAE the call never fires, so `gosiResolved` stays false and
   * neither marker renders.
   */
  const [gosiPending, setGosiPending] = useState(false);
  const [gosiResolved, setGosiResolved] = useState(false);

  /**
   * 🔴 CO-261 — the staleness stamp for EVERY fetch on this screen.
   *
   * `fetchData` is keyed on [year, month, t, country], so changing the period starts a SECOND
   * run while the first is still in flight; the refresh button and `handleResolve` can start a
   * third. Without a stamp the SLOWER response wins the last write, and because the CO-256 reset
   * now blanks each panel up front, the losing period's data lands UNDER the new period's heading
   * with nothing to contradict it.
   *
   * The GOSI call is the acute case — it is fired un-awaited and is the slowest thing here
   * (GosiService.GetPanelDataAsync is an N+1, one decrypting round-trip per employee, ~1-2.5 s),
   * so Aug→Sep inside that window reliably lets August's red alert and August's SAR figure
   * repaint under the September heading. That is a false statutory position, not a cosmetic
   * staleness — exactly the class CO-256 exists to close.
   *
   * A monotonic counter rather than a cleanup flag: `fetchData` is not only driven by an effect
   * (refresh button, post-resolve refetch), so there is no cleanup callback that covers every
   * caller. Incrementing on entry invalidates whatever was in flight, whoever started it.
   */
  const requestSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestSeq = ++requestSeqRef.current;
    const isStale = () => requestSeqRef.current !== requestSeq;
    setLoading(true);
    setError(null);
    /**
     * CO-256 — every panel below is written ONLY on success, so without this reset a failed or
     * partially-failed period silently inherits the previous one's numbers. The period picker and
     * the country are both fetch inputs, so "August, 3 employees, SAR 6,000 of missing statutory
     * base" survived a switch to September whose GOSI call 500'd, and the Overview badge — which
     * renders OUTSIDE the `loading` gate below — kept counting it. On a compliance screen that
     * leads with a money figure and cites GOSI arrears, that is a false statutory position, not a
     * cosmetic staleness. A failed period must read as NO SIGNAL, never as last period's signal.
     */
    setStatutoryBaseHealth(null);
    setDashboard(null);
    setBreaches([]);
    setProbationWarnings([]);
    setHistory([]);
    setContributions([]);
    setEmiratization(null);
    setPreflight(null);
    // Settle independently: some endpoints are UAE-only and 404 for KSA companies.
    // A rejection on one must not blank the others (e.g. KSA probation warnings).
    const settled = await Promise.allSettled([
      getComplianceDashboard(year, month),
      getDeductionBreaches(year, month),
      getProbationWarnings(),
      getComplianceHistory(),
      getContributions(year, month),
      getEmiratizationSummary(),
      getSifPreflight(year, month),
    ]);
    // A newer period (or a newer refresh) started while this batch was in flight — drop it whole.
    // Partial application is not an option: these panels are read together as one period.
    if (isStale()) return;
    const [dashRes, breachRes, probRes, histRes, contribRes, emiratizationRes, preflightRes] = settled;

    if (dashRes.status === 'fulfilled' && dashRes.value.errors.length === 0) setDashboard(dashRes.value.payload);
    if (breachRes.status === 'fulfilled' && breachRes.value.errors.length === 0) setBreaches(breachRes.value.payload);
    if (probRes.status === 'fulfilled' && probRes.value.errors.length === 0) setProbationWarnings(probRes.value.payload);
    if (histRes.status === 'fulfilled' && histRes.value.errors.length === 0) setHistory(histRes.value.payload);
    if (contribRes.status === 'fulfilled' && contribRes.value.errors.length === 0) setContributions(contribRes.value.payload);
    if (emiratizationRes.status === 'fulfilled' && emiratizationRes.value.errors.length === 0) setEmiratization(emiratizationRes.value.payload);
    if (preflightRes.status === 'fulfilled' && preflightRes.value.errors.length === 0) setPreflight(preflightRes.value.payload);

    // Surface an error banner only on a genuine outage — every call failed (rejected or
    // returned errors). The per-country 404s on UAE-only endpoints never trip this for KSA,
    // because the probation call still resolves successfully.
    const allFailed = settled.every(r => r.status === 'rejected' || r.value.errors.length > 0);
    if (allFailed) {
      const rejected = settled.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
      setError(getApiErrorMessage(rejected?.reason, t, t('common.error')));
    }

    // CO-256. Kept OUT of the allSettled batch above on purpose: this endpoint is KSA-guarded, so
    // folding it in would either add a guaranteed 404 for every UAE tenant or (if stubbed to a
    // resolved null for UAE) permanently defeat the `allFailed` outage banner, which requires
    // EVERY call to have failed.
    //
    // ⚠️ And NOT awaited (CO-261). GosiService.GetPanelDataAsync is an N+1 — one decrypting
    // round-trip per non-archived employee — and this screen now calls it on EVERY load and EVERY
    // year/month change, so awaiting it held the whole Compliance Center behind a per-employee
    // serial scan (estimated ~1-2.5 s at a few hundred employees). The statutory-base alert is
    // purely additive: it appears when the answer lands, and nothing else on the tab reads it.
    setLoading(false);

    // CO-261 — the statutory-base slot is now busy in its own right, independently of `loading`.
    // Armed BEFORE the call is fired so there is no frame in which the page is released and the
    // slot is silently empty; cleared on every exit path of the IIFE below.
    setGosiPending(country === 'KSA');
    setGosiResolved(false);

    if (country === 'KSA') {
      void (async () => {
        try {
          const gosiRes = await getGosiPanelData(year, month);
          // The period moved on while this (slow, N+1) call was in flight. Writing here is the
          // stale-signal bug in its acute form — an older period's alert repainting under the
          // newer heading — so drop the answer entirely; the newer run owns the state.
          if (isStale()) return;
          // Both branches write: an errors-carrying envelope is a failure too, and leaving the
          // state untouched there is exactly how a stale figure re-enters.
          setStatutoryBaseHealth(gosiRes.errors.length === 0 ? (gosiRes.payload?.statutoryBaseHealth ?? null) : null);
        } catch {
          if (isStale()) return;
          // A GOSI outage must not blank the rest of the compliance center; the panel itself reports it.
          // But it MUST leave the statutory-base signal empty rather than stale.
          setStatutoryBaseHealth(null);
        } finally {
          // EVERY exit path lands here, including the two `isStale()` early returns above — a
          // pending marker that outlives its fetch is exactly as misleading as one that never
          // appeared. The stale guard is preserved: a superseded run must not clear the marker a
          // NEWER run has already re-armed, so only the run that still owns the state settles it.
          if (!isStale()) {
            setGosiPending(false);
            setGosiResolved(true);
          }
        }
      })();
    }
  }, [year, month, t, country]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleResolve = async (eventId: number) => {
    try {
      const result = await resolveComplianceEvent(eventId);
      if (result.errors.length === 0) await fetchData();
      else setError(getApiErrorMessage(result, t));
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('common.error')));
    }
  };

  // A null dashboard means no data — count zero issues (not the "1 + 1" the UAE
  // WPS/GPSSA ternaries would otherwise yield). WPS/GPSSA only count for UAE.
  // CO-256 — counted OUTSIDE the dashboard gate. On KSA the dashboard is always null (UAE-only
  // endpoint), so anything inside that ternary is unreachable for exactly the tenants this guard
  // exists for, and the Overview tab carried no badge while GOSI was under-contributing.
  const statutoryBaseIssueCount =
    statutoryBaseHealth && !statutoryBaseHealth.isHealthy ? 1 : 0;

  const issueCount = statutoryBaseIssueCount + (!dashboard ? 0 :
    dashboard.deductionCapBreachCount +
    dashboard.probationWarningCount +
    (dashboard.basicSalaryInvariantHealthy === false ? 1 : 0) +
    (country === 'UAE'
      ? (dashboard.wpsStatus && !dashboard.wpsStatus.isSubmitted ? 1 : 0) +
        (dashboard.gpssaFullyCovered ? 0 : 1)
      : 0));

  const tabs: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }>; badge?: number }[] = [
    {
      id: 'overview',
      label: t('payroll.complianceCenter.tabs.overview', 'Overview'),
      icon: ShieldCheck,
      badge: issueCount > 0 ? issueCount : undefined,
    },
    // CCHI is a KSA-only concept.
    ...(country === 'KSA'
      ? [{ id: 'cchi' as TabId, label: t('payroll.complianceCenter.tabs.cchi', 'CCHI'), icon: HeartPulse }]
      : []),
    // Compliance Issues (deduction cap + probation) is UAE-shaped; KSA surfaces probation in the Overview.
    ...(country === 'UAE'
      ? [{ id: 'compliance-issues' as TabId, label: t('payroll.complianceCenter.tabs.complianceIssues', 'Compliance Issues'), icon: AlertTriangle }]
      : []),
    { id: 'events', label: t('payroll.complianceCenter.tabs.events', 'Event Log'), icon: TrendingUp },
  ];

  return (
    <PageLayout
      title={t('payroll.complianceCenter.title', 'Compliance Center')}
      icon={ShieldCheck}
      accentColor="rose"
    >
      <div className="space-y-6">
        <div className="flex items-center gap-4 flex-wrap">
          <YearMonthPicker
            date={new Date(year, month - 1, 1)}
            onChange={d => {
              setYear(d.getFullYear());
              setMonth(d.getMonth() + 1);
            }}
          />
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 text-sm border border-input rounded-md hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="flex border-b border-border overflow-x-auto" data-testid="compliance-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge !== undefined && (
                <span className="ms-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingState title={t('common.loading')} size="md" />
        ) : (
          <>
            {/*
              CO-261 — machine-readable state of the statutory-base slot. Rendered here, inside the
              released content branch, because that is precisely the window the page had no way to
              describe: `loading` is already false and the slot is empty for reasons a reader
              cannot tell apart.

              🔴 ALL THREE markers below are KSA-ONLY BY CONSTRUCTION. `gosiPending`/`gosiResolved`
              are only ever set inside the KSA IIFE in `fetchData`, so on any other compliance
              country NONE of them ever render. `useComplianceCountry` (hooks/useComplianceCountry.ts:7)
              returns 'UAE' for a UAE tenant and 'None' while redux `s.user.company` is still
              unhydrated — in both cases the GOSI call never fires. A settle helper that waits
              unconditionally for `statutory-base-settled` (or for either sibling marker) therefore
              HANGS on a UAE tenant and on an unhydrated store; a spec must gate that wait on the
              tenant's compliance country being KSA.

              Deliberately styled INLINE rather than with the project's `.sr-only`
              (assets/styles/utilities.css:193). Both are off-screen-but-measurable — that utility
              is the clip-rect flavour, not `display:none`, so it would work — but a marker whose
              observability depends on a global stylesheet's cascade is one refactor away from
              being silently unobservable, and this whole defect exists because a silent state
              looked healthy. The geometry is a 1x1 clipped box: non-empty bounding rect, so it
              satisfies a visibility wait, while showing nothing to a sighted user. aria-busy
              carries the same fact to assistive tech.
            */}
            {gosiPending && (
              <div
                data-testid="statutory-base-pending"
                data-loading="true"
                aria-busy="true"
                aria-live="polite"
                style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
              >
                {t('payroll.statutoryBase.checking', 'Checking statutory base…')}
              </div>
            )}
            {/*
              The terminal counterpart: absence stated as a POSITIVE fact. It means all three of —
              this run's GOSI call reached a terminal state (`gosiResolved`), it is no longer in
              flight (`!gosiPending`), and the answer carries nothing to show, i.e. either no
              health object at all (KSA outage / empty envelope) or one that is healthy. It is
              KSA-only by construction: `gosiResolved` is only ever set inside the KSA IIFE.
              Note this is NOT "healthy" — a GOSI outage also blanks the slot — it is "the fetch is
              over and the slot is legitimately empty", which is the distinction that was missing.
            */}
            {!gosiPending && gosiResolved && !(statutoryBaseHealth && !statutoryBaseHealth.isHealthy) && (
              <div
                data-testid="statutory-base-clean"
                data-resolved="true"
                data-health={statutoryBaseHealth ? 'healthy' : 'none'}
                style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
              />
            )}
            {/*
              CO-261 — the UNCONDITIONAL terminal marker, and the one a settle contract should key
              off. `statutory-base-clean` above only covers the "nothing to show" outcome, and the
              UNHEALTHY outcome's only evidence is StatutoryBaseAlert's `statutory-base-alert`
              (components/StatutoryBaseAlert.tsx:110) — which renders ONLY on the overview tab,
              while the clean marker renders on all four. So on the unhealthy path, on the other
              three tabs, there was NO selector at all meaning "the GOSI fetch is over": exactly the
              in-flight-vs-settled ambiguity CO-261 exists to close.

              This node renders on every terminal outcome regardless of health, on every tab, and
              carries the outcome in `data-health` ('healthy' | 'unhealthy' | 'none') so a spec
              waits on ONE selector and then reads the answer, instead of racing two mutually
              exclusive ones. Same 1x1 inline-styled visually-hidden treatment as its siblings, and
              the same KSA-only caveat noted at the top of this block.
            */}
            {!gosiPending && gosiResolved && (
              <div
                data-testid="statutory-base-settled"
                data-resolved="true"
                data-health={statutoryBaseHealth ? (statutoryBaseHealth.isHealthy ? 'healthy' : 'unhealthy') : 'none'}
                style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}
              />
            )}
            {activeTab === 'overview' && (
              <OverviewTab
                dashboard={dashboard}
                emiratization={emiratization}
                contributions={contributions}
                probationWarnings={probationWarnings}
                preflight={preflight}
                statutoryBaseHealth={statutoryBaseHealth}
                onJumpToTab={setActiveTab}
              />
            )}
            {activeTab === 'cchi' && (
              <KsaCchiPanel onError={setError} />
            )}
            {activeTab === 'compliance-issues' && (
              <ComplianceIssuesTab
                breaches={breaches}
                probationWarnings={probationWarnings}
                emiratization={emiratization}
                emiratizationLoading={loading && emiratization === null}
              />
            )}
            {activeTab === 'events' && (
              <EventLogTab history={history} onResolve={handleResolve} />
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default ComplianceCenter;
